import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides, categoryLabels } from "@/mocks/movies";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [broken, setBroken] = useState<Record<number, boolean>>({});
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((index: number) => {
    setActive(((index % heroSlides.length) + heroSlides.length) % heroSlides.length);
  }, []);

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  useEffect(() => {
    timer.current = setInterval(() => {
      setActive((v) => (v + 1) % heroSlides.length);
    }, 5000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  const slide = heroSlides[active];
  if (!slide) return null;

  return (
    <section className="relative h-[420px] w-full overflow-hidden md:h-[600px]">
      {heroSlides.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "opacity-0"
          }`}
        >
          {!broken[item.id] && (
            <img
              src={item.backdrop}
              alt={`${item.title}剧照`}
              title={`${item.title} - 91在线视频高清影视`}
              onError={() => setBroken((prev) => ({ ...prev, [item.id]: true }))}
              className="h-full w-full object-cover object-top"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50/90 via-background-50/40 to-transparent" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-screen-2xl flex-col justify-end px-4 pb-14 md:px-8 md:pb-20">
        <div key={slide.id} className="max-w-2xl animate-fade-up">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-700 text-background-50">
              编辑推荐
            </span>
            <span className="rounded-md border border-background-300/60 bg-background-100/70 px-2.5 py-1 text-xs text-foreground-700">
              {categoryLabels[slide.category]}
            </span>
            <span className="flex items-center gap-1 text-xs font-600 text-accent-500">
              <i className="ri-star-fill text-[10px]" />
              {slide.rating.toFixed(1)}
            </span>
          </div>
          <h2 className="font-heading text-3xl font-700 leading-tight text-foreground-950 md:text-5xl">
            {slide.title}
          </h2>
          <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-foreground-600 md:mt-4 md:text-base">
            {slide.description}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3 md:mt-7">
            <Link
              to={`/detail/${slide.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-600 text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-fill text-lg" />
              查看详情
            </Link>
            <span className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 px-5 py-2.5 text-sm text-foreground-600">
              <i className="ri-eye-line" />
              {slide.views}万次播放
            </span>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-4">
          <button
            type="button"
            aria-label="上一张"
            onClick={prev}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300/60 bg-background-100/70 text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500 md:h-10 md:w-10"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={next}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300/60 bg-background-100/70 text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-500 md:h-10 md:w-10"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
          <div className="flex items-center gap-2">
            {heroSlides.map((item, index) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到第${index + 1}张`}
                onClick={() => goTo(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === active ? "w-8 bg-accent-500" : "w-4 bg-background-400"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}